import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

import {
  getTheme,
} from '../theme/themes'

import {
  loadPreferences,
  resetPreferences,
  savePreferences,
  type GenesisPreferences,
  type PanelSide,
} from './preferences'

interface LayoutContextValue {
  preferences:
    GenesisPreferences

  update:
    (
      patch:
        Partial<GenesisPreferences>,
    ) => void

  movePanel:
    (
      id: string,
      direction:
        'UP' | 'DOWN',
    ) => void

  moveSide:
    (
      id: string,
      side: PanelSide,
    ) => void

  setPanelHeight:
    (
      id: string,
      value: number,
    ) => void

  restore: () => void
}

const Context =
  createContext<
    LayoutContextValue | null
  >(null)

export function LayoutProvider({
  children,
}: {
  children: ReactNode
}) {
  const [
    preferences,
    setPreferences,
  ] =
    useState(
      () =>
        loadPreferences(),
    )

  useEffect(() => {
    savePreferences(
      preferences,
    )

    const root =
      document.documentElement

    const theme =
      getTheme(
        preferences.theme,
      )

    for (
      const [
        key,
        value,
      ]
      of Object.entries(
        theme.variables,
      )
    ) {
      root.style.setProperty(
        key,
        value,
      )
    }

    root.style.setProperty(
      '--font-family',
      preferences.fontFamily,
    )


    root.style.setProperty(
      '--r7-app-background',
      preferences.backgroundBaseColor,
    )

    root.style.setProperty(
      '--r7-background-intensity',
      `${preferences.backgroundIntensity}%`,
    )

    root.style.setProperty(
      '--r7-environment-contrast',
      String(
        Math.max(
          0,
          Math.min(
            100,
            preferences.backgroundContrast,
          ),
        ) / 100,
      ),
    )

    root.style.setProperty(
      '--system-button-columns',
      String(
        preferences.systemButtonColumns,
      ),
    )

    root.style.setProperty(
      '--system-button-text-scale',
      String(
        preferences.systemButtonTextScale,
      ),
    )

    root.style.setProperty(
      '--engineering-text-scale',
      String(
        preferences.engineeringTextScale,
      ),
    )

    root.style.setProperty(
      '--clock-time-scale',
      String(
        preferences.clockTimeScale,
      ),
    )

    root.style.setProperty(
      '--clock-date-scale',
      String(
        preferences.clockDateScale,
      ),
    )

    root.style.setProperty(
      '--clock-time-color',
      preferences.clockTimeColor,
    )

    root.style.setProperty(
      '--clock-date-color',
      preferences.clockDateColor,
    )

    root.style.setProperty(
      '--user-text-color',
      preferences.textColor,
    )


    root.style.setProperty(
      '--module-bg-user',
      preferences.moduleBackgroundColor,
    )

    root.style.setProperty(
      '--conversation-bg-user',
      preferences.conversationBackgroundColor,
    )

    root.style.setProperty(
      '--clock-user-color',
      preferences.clockColor,
    )

    root.style.setProperty(
      '--top-button-bg-user',
      preferences.topButtonBackgroundColor,
    )

    root.style.setProperty(
      '--top-button-text-user',
      preferences.topButtonTextColor,
    )

    root.style.setProperty(
      '--system-button-columns',
      String(preferences.systemButtonColumns),
    )

    root.style.setProperty(
      '--effective-text',
      preferences.textColor || 'var(--text)',
    )

    document.body.dataset.layoutPreset =
      preferences.layoutPreset

    if (
      !preferences.textColor ||
      !/^#[0-9a-fA-F]{6}$/.test(
        preferences.textColor,
      )
    ) {
      root.style.setProperty(
        '--effective-text',
        preferences.layoutPreset === 'LIGHT_PRO'
          ? '#172832'
          : '#e4edf5',
      )
    }

    root.style.setProperty(
      '--scale',
      String(
        preferences.generalScale,
      ),
    )

    root.style.setProperty(
      '--button-scale',
      String(
        preferences.buttonScale,
      ),
    )


    root.style.setProperty(
      '--button-physical-scale',
      String(
        preferences.buttonPhysicalScale,
      ),
    )


    root.style.setProperty(
      '--system-button-text-scale',
      String(
        preferences.systemButtonTextScale,
      ),
    )

    root.style.setProperty(
      '--engineering-text-scale',
      String(
        preferences.engineeringTextScale,
      ),
    )

    root.style.setProperty(
      '--clock-time-scale',
      String(
        preferences.clockTimeScale,
      ),
    )

    root.style.setProperty(
      '--clock-date-scale',
      String(
        preferences.clockDateScale,
      ),
    )

    root.style.setProperty(
      '--clock-time-color',
      preferences.clockTimeColor,
    )

    root.style.setProperty(
      '--clock-date-color',
      preferences.clockDateColor,
    )

    root.style.setProperty(
      '--description-scale',
      String(
        preferences.descriptionScale,
      ),
    )

    root.style.setProperty(
      '--density',
      String(
        preferences.density,
      ),
    )

    root.style.setProperty(
      '--radius',
      `${preferences.radius}px`,
    )
  }, [preferences])

  function update(
    patch:
      Partial<GenesisPreferences>,
  ) {
    setPreferences(
      current => ({
        ...current,
        ...patch,
      }),
    )
  }

  function movePanel(
    id: string,
    direction:
      'UP' | 'DOWN',
  ) {
    setPreferences(
      current => {
        const panels =
          [...current.panels]

        const item =
          panels.find(
            p =>
              p.id === id,
          )

        if (!item) {
          return current
        }

        const sameSide =
          panels
            .filter(
              p =>
                p.side ===
                item.side,
            )
            .sort(
              (a, b) =>
                a.order -
                b.order,
            )

        const index =
          sameSide.findIndex(
            p =>
              p.id === id,
          )

        const target =
          direction === 'UP'
            ? index - 1
            : index + 1

        if (
          target < 0 ||
          target >=
            sameSide.length
        ) {
          return current
        }

        const swap =
          sameSide[target]

        const currentOrder =
          item.order

        item.order =
          swap.order

        swap.order =
          currentOrder

        return {
          ...current,
          panels,
        }
      },
    )
  }

  function moveSide(
    id: string,
    side: PanelSide,
  ) {
    setPreferences(
      current => ({
        ...current,

        panels:
          current.panels.map(
            p =>
              p.id === id
                ? {
                    ...p,
                    side,
                  }
                : p,
          ),
      }),
    )
  }

  function setPanelHeight(
    id: string,
    value: number,
  ) {
    setPreferences(
      current => ({
        ...current,

        panelHeights: {
          ...current.panelHeights,

          [id]:
            Math.max(
              120,
              Math.min(
                900,
                value,
              ),
            ),
        },
      }),
    )
  }

  function restore() {
    setPreferences(
      resetPreferences(),
    )
  }

  return (
    <Context.Provider
      value={{
        preferences,
        update,
        movePanel,
        moveSide,
        setPanelHeight,
        restore,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export function useLayout() {
  const context =
    useContext(Context)

  if (!context) {
    throw new Error(
      'LayoutProvider missing',
    )
  }

  return context
}
